import {GifPicker} from './GifPicker';
import {CustomEmote,EmoteGlyph,customEmoteId} from './CustomEmotes';
import {emojiGroups,emojiMatches,emojiLabel,emojiShortcode,animatedEmote,loadEmoji} from './emoji-catalog';
import {AnimatedEmote} from './EmojiSuggestions';
import {StickerPicker} from './Stickers';
import {createPortal} from 'react-dom';
import React, { useEffect, useRef, useState, useLayoutEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import { useLang } from './i18n';

export function EmojiPicker({
  onPick,
  onClose,
  mode = 'all',
  closing = false,
  initialTab = 'emoji',
  anchor,onStickers,customEmotes=[], onConfigureGifs
}: {
  onPick: (val: string, isGif?: boolean) => void;
  onClose: () => void;
  mode?: 'all' | 'emoji-only';
  closing?: boolean;
  initialTab?: 'emoji' | 'gif' | 'sticker';
  anchor?:{x:number;y:number};
  onStickers?:()=>void;
  customEmotes?:CustomEmote[];
  onConfigureGifs?:()=>void;
}) {
  const [lang, , tr] = useLang();
  const [tab, setTab] = useState<'emoji' | 'gif' | 'sticker'>(mode === 'emoji-only' ? 'emoji' : initialTab);
  const [query, setQuery] = useState(''),[favoriteMode,setFavoriteMode]=useState(false);
  const [,emojiReady]=useState(0);useEffect(()=>{void loadEmoji().then(()=>emojiReady(x=>x+1)).catch(()=>{})},[]);
  const emotesById=useMemo(()=>new Map(customEmotes.map(emote=>[emote.id,emote])),[customEmotes]);
  const read=(key:string)=>{try{const values=JSON.parse(localStorage.getItem(key)||'[]');return Array.isArray(values)?values.filter(v=>typeof v==='string'):[]}catch{return []}};
  const [favorites,setFavorites]=useState<string[]>(()=>read('malvo-emoji-favorites')),[recent,setRecent]=useState<string[]>(()=>read('malvo-emoji-recent'));
  const identity=(value:string)=>customEmoteId(value)?'emote:'+customEmoteId(value):value;
  const restore=(values:string[])=>values.map(value=>value.startsWith('emote:')?emotesById.get(value.slice(6))?.token:value).filter((value):value is string=>!!value);
  const groups=[{name:'Favorites',nameDe:'Favoriten',items:restore(favorites)},{name:'Recent',nameDe:'Zuletzt verwendet',items:restore(recent)},{name:'Streaming emotes',nameDe:'Twitch-Emotes',items:customEmotes.filter(em=>!!em.source).map(em=>em.token)},{name:'Global emotes',nameDe:'Globale Emotes',items:customEmotes.filter(em=>!em.communityId&&!em.source&&!em.category).map(em=>em.token)},{name:'Server emotes',nameDe:'Server-Emotes',items:customEmotes.filter(em=>!!em.communityId).map(em=>em.token)},...[['classic','MALVO Classics','MALVO Klassiker'],['gaming','Gaming','Gaming'],['feelings','Feelings','Gefühle'],['reactions','Reactions','Reaktionen'],['social','Together','Zusammen']].map(([category,name,nameDe])=>({name,nameDe,items:customEmotes.filter(em=>em.category===category).map(em=>em.token)})),...emojiGroups];
  const [hovered,setHovered]=useState<string|null>(null);
  const shortcode=(value:string)=>{const id=customEmoteId(value),em=id?emotesById.get(id):undefined;return em?':'+em.name+':':emojiShortcode(value);};
  const label=(value:string)=>(()=>{const id=customEmoteId(value),em=id?emotesById.get(id):undefined;return em?.displayName||em?.name})()||emojiLabel(value);
  function pick(value:string,isGif=false){if(!isGif){const id=identity(value),next=[id,...recent.filter(v=>v!==id)].slice(0,40);setRecent(next);try{localStorage.setItem('malvo-emoji-recent',JSON.stringify(next))}catch{}}onPick(value,isGif)}
  function toggleFavorite(value:string){const id=identity(value),next=favorites.includes(id)?favorites.filter(v=>v!==id):[id,...favorites].slice(0,100);setFavorites(next);try{localStorage.setItem('malvo-emoji-favorites',JSON.stringify(next))}catch{}}

  const [category, setCategory] = useState('Trending'),[emojiLimit,setEmojiLimit]=useState(100);
  useEffect(()=>setEmojiLimit(100),[category,query]);


  const ref = useRef<HTMLDivElement>(null);
  const [position,setPosition]=useState({left:8,top:8});
  useLayoutEffect(()=>{const place=()=>{const r=ref.current?.getBoundingClientRect();if(!r)return;const x=anchor?.x??innerWidth-390,y=anchor?.y??innerHeight-80;setPosition({left:Math.max(8,Math.min(x,innerWidth-r.width-8)),top:Math.max(8,Math.min(y-r.height-10,innerHeight-r.height-8))});};place();window.addEventListener('resize',place);return()=>window.removeEventListener('resize',place);},[anchor,tab]);

  useEffect(() => {
    const close = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', key);
    };
  }, [onClose]);

  return createPortal(
    <div style={position} ref={ref} className={"chat-emoji-picker flackle-emoji-picker "+(closing?"is-closing":"")} role="dialog" aria-label="Emoji & GIF Picker">
      <header>
        {mode === 'all' ? (
          <div className="picker-tabs">
            {<button type="button" className={'picker-tab '+(tab==='sticker'?'active':'')} onClick={()=>{setTab('sticker');setQuery('');}}>{tr('Stickers','Sticker')}</button>}
            <button
              type="button"
              className={'picker-tab ' + (tab === 'emoji' ? 'active' : '')}
              onClick={() => {
                setTab('emoji');
                setCategory('All');
                setQuery('');
              }}
            >
              Emojis
            </button>
            <button
              type="button"
              className={'picker-tab ' + (tab === 'gif' ? 'active' : '')}
              onClick={() => {
                setTab('gif');
                setCategory('Trending');
                setQuery('');
              }}
            >
              GIFs
            </button>
          </div>
        ) : (
          <b>Emojis</b>
        )}
        <button type="button" onClick={onClose} aria-label={tr('Close', 'Schließen')}>
          ✕
        </button>
      </header>

      {tab==='sticker'?<StickerPicker embedded onClose={onClose} closing={false} onPick={body=>onPick(body,true)} onTab={setTab}/>:tab === 'emoji' ? (
        <>
          <div className="picker-search-box">
            <Search size={15} />
            <input
              autoFocus
              placeholder={tr('Search emojis...', 'Emoji suchen...')}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <nav>
            {['All', ...groups.filter(g=>g.name!=='Animated'||mode==='all').map(g => g.name)].map(n => {
              const gObj = groups.find(g => g.name === n);
              const label = n === 'All' ? tr('All', 'Alle') : lang === 'de' && gObj ? gObj.nameDe : n;
              return (
                <button
                  type="button"
                  className={n === category || (category === 'Trending' && n === 'All') ? 'active' : ''}
                  key={n}
                  onClick={() => setCategory(n)}
                >
                  {label}
                </button>
              );
            })}
          </nav>
          <div className="emoji-scroll">
            {groups
              .filter(g => (g.name!=='Animated'||mode==='all')&&(category === 'All' || category === 'Trending' || g.name === category))
              .map(g => {
                const filtered = g.items.filter(
                  i => !query || label(i).toLowerCase().includes(query.toLowerCase()) || emojiMatches(i,query) || g.name.toLowerCase().includes(query.toLowerCase()) || g.nameDe.toLowerCase().includes(query.toLowerCase())
                );
                const items=filtered.slice(0,emojiLimit);
                return items.length || g===groups[0] ? (
                  <section key={g.name}>
                    <h4>{lang === 'de' ? g.nameDe : g.name}<small className="emoji-group-count">{filtered.length}</small>{g===groups[0]&&<button type="button" className="emoji-favorite-mode" aria-pressed={favoriteMode} onClick={()=>setFavoriteMode(!favoriteMode)}>{favoriteMode?tr('Done','Fertig'):tr('Manage favorites','Favoriten bearbeiten')}</button>}</h4>
                    <div className="emoji-grid">
                      {items.map((i, idx) => (
                        <button type="button" aria-label={label(i)} title={label(i)+' · '+tr('Right-click: toggle favorite','Rechtsklick: Favorit umschalten')} onMouseEnter={()=>setHovered(i)} onFocus={()=>setHovered(i)} aria-pressed={favorites.includes(identity(i))} onContextMenu={e=>{e.preventDefault();toggleFavorite(i)}} key={i + '-' + idx} onClick={() => favoriteMode?toggleFavorite(i):pick(i, false)}>
                          {customEmoteId(i)?<EmotePreview emote={emotesById.get(customEmoteId(i)!)!}/>:animatedEmote(i)?<AnimatedEmote token={i}/>:i}
                        </button>
                      ))}
                    </div>{filtered.length>items.length&&<button type="button" className="emoji-group-more" onClick={()=>{if(category==='All'||category==='Trending')setCategory(g.name);else setEmojiLimit(value=>value+100)}}>{tr('Show more','Mehr anzeigen')} ({filtered.length-items.length})</button>}
                  </section>
                ) : null;
              })}
          </div><footer className="emoji-catalog-footer" aria-live="polite">{hovered?<><span className="emoji-footer-preview">{customEmoteId(hovered)?<EmotePreview emote={emotesById.get(customEmoteId(hovered)!)!}/>:animatedEmote(hovered)?<AnimatedEmote token={hovered}/>:hovered}</span><span className="emoji-footer-name"><b>{shortcode(hovered)||label(hovered)}</b>{shortcode(hovered)&&<small>{label(hovered)}</small>}</span></>:<span className="emoji-footer-hint">{tr('Tip: type :name: in the chat, e.g. :fire:','Tipp: Tippe :name: im Chat, z. B. :fire:')}</span>}</footer>
        </>
      ) : (
        <GifPicker onPick={value=>onPick(value,true)} onConfigure={onConfigureGifs}/>
      )}
    </div>,document.fullscreenElement||document.body
  );
}

function EmotePreview({emote}:{emote:CustomEmote}){if(!emote)return null;return <img src={emote.poster} alt={emote.displayName||emote.name} loading="lazy" width={28} height={28} onMouseEnter={e=>{if(emote.animated&&!matchMedia('(prefers-reduced-motion: reduce)').matches)e.currentTarget.src=emote.url}} onMouseLeave={e=>{e.currentTarget.src=emote.poster}}/>}
